import { CONSTRUCTOR_SECTIONS } from "src/constants/constructor";
import type {
  ProductSuggestion,
  QueryParams,
  QueryResult,
} from "src/types/adapters/query.type";
import type { SearchAdapterOptions } from "src/types/adapters/search.type";
import { ShopType } from "src/utils/genderLocale/genderLocale";
import type { PropsWithConstructorClient } from ".";
import {
  DEFAULT_FACET,
  PRODUCT_SUGGESTION_DATA_FIELDS,
  SUGGESTION_DATA_FIELDS,
} from "./utils/constants";
import { buildSuggestions, mapProductSuggestions } from "./utils/helpers";

type QueryProps = PropsWithConstructorClient<{
  options: SearchAdapterOptions;
  query: string;
  params?: QueryParams;
}>;

const MERGE_FIELDS = new Set([
  ...PRODUCT_SUGGESTION_DATA_FIELDS,
  ...SUGGESTION_DATA_FIELDS,
]);

export async function query({
  client,
  query,
  params,
  options,
}: QueryProps): Promise<QueryResult> {
  try {
    const [queryResponse, facetsResponse] = await Promise.all([
      client.autocomplete(query, {
        resultsPerSection: {
          [CONSTRUCTOR_SECTIONS.BRANDS]: 5,
          [CONSTRUCTOR_SECTIONS.CATEGORIES]: 5,
          [CONSTRUCTOR_SECTIONS.COLLECTIONS]: 5,
          [CONSTRUCTOR_SECTIONS.PRODUCTS]: 6,
        },
        fmtOptions: {
          fields: [...MERGE_FIELDS],
        },
        filters: {
          genders:
            params?.gender && params?.gender !== ShopType.All
              ? params?.gender
              : undefined,
        },
      }),
      client.browse(DEFAULT_FACET.facetName, DEFAULT_FACET.facetValue, {
        section: CONSTRUCTOR_SECTIONS.PRODUCTS,
        filters: {
          genders:
            params?.gender && params?.gender !== ShopType.All
              ? params?.gender
              : undefined,
        },
        resultsPerPage: 1,
      }),
    ]);

    const suggestionItems = buildSuggestions(
      queryResponse.sections,
      facetsResponse.response.facets
    );

    const productItems =
      queryResponse.sections[CONSTRUCTOR_SECTIONS.PRODUCTS] || [];
    const totalProducts =
      queryResponse.total_num_results_per_section?.[
        CONSTRUCTOR_SECTIONS.PRODUCTS
      ] || 0;

    return {
      query,
      resultId: queryResponse.result_id,
      products: productItems.flatMap<ProductSuggestion>(mapProductSuggestions),
      totalProductsResult: totalProducts,
      suggestions: suggestionItems,
    };
  } catch (error) {
    console.error(
      "[Constructor Adapter] An error occurred while fetching query results:",
      error
    );

    return {
      query,
      totalProductsResult: 0,
    };
  }
}
